
import React, { useState, useEffect } from 'react';
import {
  Bot,
  RefreshCw,
  Search,
  UserPlus,
  BrainCircuit,
  Database,
  History,
  X,
  Save,
  PlusCircle,
  Phone,
  Mail,
  Globe,
  Trash2,
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext'; // CORRECTED IMPORT
import { knowledgeGraph } from '../services/knowledgeGraphService';
import { searchArtist } from '../services/musicBrainzService';
import { Artist, Lead as LeadType, LeadQuery as LeadQueryType } from '../types';
import { API_BASE_URL } from '../config';

// This interface is for the data coming from the AI service
interface AILeadResult {
  id: string;
  name: string;
  reason: string;
}

// This is a more detailed type for our internal representation of leads
interface DetailedLead extends LeadType {
  email?: string;
  phone?: string;
  website?: string;
}

// Modal Component for Adding/Editing Leads
const LeadEnrichmentModal: React.FC<{
  lead: DetailedLead | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (lead: DetailedLead) => Promise<boolean>;
}> = ({ lead, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState<DetailedLead | null>(null);

  useEffect(() => {
    if (lead) {
      setFormData({
        ...lead,
        email: lead.email || '',
        phone: lead.phone || '',
        website: lead.website || '',
      });
    }
  }, [lead, isOpen]);

  if (!isOpen || !formData) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (formData) setFormData({ ...formData, [name]: value });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.name.trim()) {
      alert('Lead name is required.');
      return;
    }
    const success = await onSave(formData);
    if (success) onClose();
  };

  return (
    <div className="fixed inset-0 bg-kala-900 bg-opacity-80 flex items-center justify-center z-50">
      <div className="bg-kala-800 p-6 rounded-xl border border-kala-700 w-full max-w-lg">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">{formData.id ? 'Enrich Lead' : 'Add Manual Lead'}</h2>
          <button onClick={onClose} className="text-kala-500 hover:text-white"><X /></button>
        </div>
        <div className="space-y-4">
          <input 
            required 
            name="name" 
            value={formData.name} 
            onChange={handleChange} 
            placeholder="Lead Name (required)" 
            className="w-full bg-kala-900 border border-kala-700 rounded-lg p-2 text-white placeholder-kala-500 focus:outline-none focus:border-kala-500" 
          />
          <textarea 
            name="bio" 
            value={formData.bio || ''} 
            onChange={handleChange} 
            placeholder="Bio / Notes" 
            className="w-full bg-kala-900 border border-kala-700 rounded-lg p-2 text-white placeholder-kala-500 focus:outline-none focus:border-kala-500" 
          />
          <input 
            type="email" 
            name="email" 
            value={formData.email || ''} 
            onChange={handleChange} 
            placeholder="Email" 
            className="w-full bg-kala-900 border border-kala-700 rounded-lg p-2 text-white placeholder-kala-500 focus:outline-none focus:border-kala-500" 
          />
          <input 
            type="tel" 
            name="phone" 
            value={formData.phone || ''} 
            onChange={handleChange} 
            placeholder="Phone" 
            className="w-full bg-kala-900 border border-kala-700 rounded-lg p-2 text-white placeholder-kala-500 focus:outline-none focus:border-kala-500" 
          />
          <input 
            type="url" 
            name="website" 
            value={formData.website || ''} 
            onChange={handleChange} 
            placeholder="Website" 
            className="w-full bg-kala-900 border border-kala-700 rounded-lg p-2 text-white placeholder-kala-500 focus:outline-none focus:border-kala-500" 
          />
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button 
            onClick={onClose} 
            className="bg-kala-700 hover:bg-kala-600 text-white font-bold px-6 py-2 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={handleSave} 
            disabled={!formData.name || !formData.name.trim()} 
            className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-6 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save size={18} /> Save Lead
          </button>
        </div>
      </div>
    </div>
  );
};


const AdminLeads: React.FC = () => {
  const { notify } = useToast();
  const { user, token } = useAuth(); // CORRECTED: Use the useAuth hook
  const [activeTab, setActiveTab] = useState<'discover' | 'leads' | 'queries'>('discover');

  // State for original props and local UI
  const [leads, setLeads] = useState<LeadType[]>([]);
  
  // State for MusicBrainz search
  const [artistQuery, setArtistQuery] = useState('');
  const [artistResults, setArtistResults] = useState<Artist[]>([]);
  const [isSearchingArtist, setIsSearchingArtist] = useState(false);

  // State for AI Lead search
  const [aiLeadQuery, setAILeadQuery] = useState('');
  const [aiLeadResults, setAILeadResults] = useState<AILeadResult[]>([]);
  const [isSearchingLeads, setIsSearchingLeads] = useState(false);
  
  // State for saved data
  const [savedLeads, setSavedLeads] = useState<DetailedLead[]>([]);
  const [savedQueries, setSavedQueries] = useState<LeadQueryType[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // State for modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentLead, setCurrentLead] = useState<DetailedLead | null>(null);
  
  // Reusable authenticated headers function
  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  });

  // Function to fetch all DB data
  const fetchData = async () => {
    if (!token) return;
    setIsLoadingData(true);
    try {
      const [leadsRes, queriesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/leads`, { headers: getAuthHeaders(), credentials: 'include' }),
        fetch(`${API_BASE_URL}/api/lead-queries`, { headers: getAuthHeaders(), credentials: 'include' }),
      ]);
      const leadsData = await leadsRes.json();
      const queriesData = await queriesRes.json();

      if (leadsRes.ok) setSavedLeads(leadsData);
      else notify(`Failed to fetch leads: ${leadsData.message}`, 'error');
      
      if (queriesRes.ok) setSavedQueries(queriesData);
      else notify(`Failed to fetch saved queries: ${queriesData.message}`, 'error');

    } catch (error) {
      console.error('Error fetching data:', error);
      notify('Error fetching data from server.', 'error');
    }
    setIsLoadingData(false);
  };
  
  useEffect(() => {
    fetchData();
  }, [token]);

  const handleSaveOrUpdateLead = async (leadData: DetailedLead): Promise<boolean> => {
    if (!token) {
      notify('Authentication error. Please log in again.', 'error');
      return false;
    }
    const isNew = !leadData.id;
    const method = isNew ? 'POST' : 'PUT';
    const endpoint = isNew ? `${API_BASE_URL}/api/leads` : `${API_BASE_URL}/api/leads/${leadData.id}`;
    
    const payload = {
      name: leadData.name,
      bio: leadData.bio || null,
      status: leadData.status || 'NEW',
      email: leadData.email || null,
      phone: leadData.phone || null,
      website: leadData.website || null,
      source: leadData.source || 'MANUAL',
      query: leadData.query || 'N/A',
    };

    try {
      const response = await fetch(endpoint, {
        method,
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'An unknown server error occurred.');
      }
      
      if (isNew) {
        setSavedLeads(prevLeads => [data, ...prevLeads]);
      } else {
        setSavedLeads(prevLeads => prevLeads.map(l => l.id === data.id ? data : l));
      }

      notify(`Lead successfully ${isNew ? 'created' : 'updated'}!`, 'success');
      return true;

    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unknown error occurred';
      console.error('❌ [AdminLeads] Error saving lead:', message);
      notify(`Error saving lead: ${message}`, 'error');
      return false;
    }
  };

  const handleArtistSearch = async () => {
    if (!artistQuery) {
      notify('Please enter an artist name to search.', 'warn');
      return;
    }
    setIsSearchingArtist(true);
    const results = await searchArtist(artistQuery);
    setArtistResults(results);
    setIsSearchingArtist(false);
    notify(`Found ${results.length} artists on MusicBrainz.`, 'success');
  };

  const handleAILeadSearch = async () => {
    if (!aiLeadQuery) {
      notify('Please enter a query for the AI.', 'warn');
      return;
    }
    if (!token) {
      notify('Authentication error. Please log in again.', 'error');
      return;
    }
    setIsSearchingLeads(true);
    setAILeadResults([]);

    try {
      const leadsResponse = await fetch(`${API_BASE_URL}/api/ai/generate-leads`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ prompt: aiLeadQuery }),
      });
      
      if (!leadsResponse.ok) {
        const errorData = await leadsResponse.json().catch(() => ({ message: 'The AI service responded with an error.' }));
        throw new Error(errorData.message || 'Failed to generate leads.');
      }

      const leadsData = await leadsResponse.json();
      setAILeadResults(leadsData);
      notify(`Found ${leadsData.length} potential leads.`, 'info');
      
      //This saves the generated leads
      await handleSaveAiResults(aiLeadQuery, leadsData);
      //This saves the query itself
      await handleSaveQuery(aiLeadQuery);


    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unknown error occurred';
      console.error('❌ [AdminLeads] AI Lead search failed:', error);
      notify(`AI search failed: ${message}`, 'error');
    }
    setIsSearchingLeads(false);
  };

  const handleSaveQuery = async (query: string) => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/lead-queries`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ query }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to save query');
      notify('AI query saved successfully!', 'success');
      fetchData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unknown error occurred';
      notify(`Error saving AI query: ${message}`, 'error');
    }
  };

  const handleSaveAiResults = async (prompt: string, results: AILeadResult[]) => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/leads`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ prompt, results }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to save AI results');
      notify('AI search results saved successfully!', 'success');
      fetchData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unknown error occurred';
      notify(`Error saving AI results: ${message}`, 'error');
    }
  };
  
  const handleDeleteQuery = async (queryId: string) => {
    if (!window.confirm('Are you sure you want to delete this query?') || !token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/lead-queries/${queryId}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders(),
          credentials: 'include',
        }
      );

      if (response.status !== 204) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to delete query.' }));
        throw new Error(errorData.message);
      }

      setSavedQueries(prevQueries => prevQueries.filter(q => q.id !== queryId));
      notify('Query deleted successfully!', 'success');

    } catch (error) {
        const message = error instanceof Error ? error.message : 'An unknown error occurred';
        notify(`Error deleting query: ${message}`, 'error');
    }
  };
  
  const addLead = (artist: Artist): boolean => {
    if (leads.find(l => l.name === artist.name)) {
      return false;
    }
    const newLead: LeadType = { ...artist, status: 'New' };
    setLeads(prevLeads => [...prevLeads, newLead]);
    return true;
  };

  const handleAddLead = async (artist: Artist) => {
    if (savedLeads.some(l => l.name === artist.name)) {
      notify(`${artist.name} is already in your saved leads list.`, 'info');
      return;
    }

    const wasAddedLocally = addLead(artist);
    if (wasAddedLocally) {
      try {
        knowledgeGraph.addLead({ ...artist, status: 'New' });
        notify(`${artist.name} added to this session. Saving to database...`, 'info');
        
        const leadToSave: DetailedLead = {
          id: '',
          name: artist.name,
          bio: artist.bio || '',
          status: 'NEW',
          email: artist.email || undefined,
          phone: artist.phone || undefined,
          website: artist.website || undefined,
          source: 'MUSICBRAINZ',
          query: artistQuery,
        };

        const success = await handleSaveOrUpdateLead(leadToSave);
        if (success) {
           notify(`${artist.name} was successfully saved to the database!`, 'success');
        }
      } catch (error) {
          const message = error instanceof Error ? error.message : 'An unknown error occurred';
          console.error('❌ [AdminLeads] Error in adding lead process:', message);
          notify(`Error in adding lead process: ${message}`, 'error');
      }
    } else {
      notify(`${artist.name} is already in your list for this session.`, 'info');
    }
  };
  
  const handleAddLeadFromAI = async (lead: AILeadResult) => {
    if (savedLeads.some(l => l.name === lead.name)) {
      notify(`${lead.name} is already in your saved leads list.`, 'info');
      return;
    }

    try {
      const leadToSave: DetailedLead = {
        id: '',
        name: lead.name,
        bio: lead.reason,
        status: 'NEW',
        source: 'AI_GENERATED',
        query: aiLeadQuery,
      };

      const success = await handleSaveOrUpdateLead(leadToSave);
      if (success) {
        notify(`${lead.name} added from AI search!`, 'success');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unknown error occurred';
      console.error('❌ [AdminLeads] Error adding AI lead:', message);
      notify(`Error adding AI lead: ${message}`, 'error');
    }
  };

  // Modal handlers
  const openEnrichModal = (lead: DetailedLead) => { 
    setCurrentLead(lead); 
    setIsModalOpen(true); 
  };
  
  const openManualAddModal = () => { 
    setCurrentLead({ 
      id: '',
      name: '',
      status: 'NEW',
      bio: '',
      email: '',
      phone: '',
      website: '',
      source: 'MANUAL',
      query: 'N/A',
    }); 
    setIsModalOpen(true); 
  };

  const TabButton = ({ tab, icon, label }: any) => (
    <button 
      onClick={() => setActiveTab(tab)} 
      className={`flex items-center gap-2 px-4 py-2 font-bold transition-colors rounded-t-lg ${activeTab === tab ? 'bg-kala-800 text-white' : 'bg-kala-900 text-kala-300 hover:text-white'}`}
    >
      {React.createElement(icon, { size: 18 })} {label}
    </button>
  );

  return (
    <div className="space-y-6">
      <LeadEnrichmentModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} lead={currentLead} onSave={handleSaveOrUpdateLead} />
      
      <div className="flex border-b border-kala-700">
        <TabButton tab="discover" icon={BrainCircuit} label="Lead Discovery" />
        <TabButton tab="leads" icon={Database} label="Saved Leads" />
        <TabButton tab="queries" icon={History} label="Saved Queries" />
      </div>

      {/* --- DISCOVER TAB --- */}
      {activeTab === 'discover' && (
        <div className="space-y-8">
          {/* AI Lead Search */}
          <div className="bg-kala-900/50 border border-indigo-700/50 rounded-xl p-6">
             <label className="block text-sm text-kala-200 font-bold mb-2 flex items-center gap-2"><BrainCircuit className="w-5 h-5 text-indigo-400" /> AI Lead Search</label>
             <div className="flex gap-2">
               <input 
                 type="text" 
                 value={aiLeadQuery} 
                 onChange={(e) => setAILeadQuery(e.target.value)} 
                 className="flex-grow bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white placeholder-kala-500 focus:outline-none focus:border-kala-500" 
                 placeholder="e.g., Find music producers in Nashville" 
               />
               <button 
                 onClick={handleAILeadSearch} 
                 disabled={isSearchingLeads} 
                 className="bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-700 text-white font-bold px-6 py-2 rounded-lg flex items-center gap-2 transition-colors"
               >
                 {isSearchingLeads ? <RefreshCw className="animate-spin" size={18} /> : <Search size={18} />}
                 {isSearchingLeads ? 'Searching...' : 'Search'}
               </button>
             </div>
          </div>
          
          {/* AI Lead Search Results */}
          {aiLeadResults.length > 0 && (
             <div className="bg-kala-800/40 border border-kala-700/80 rounded-xl">
               <div className="p-4 border-b border-kala-700"><h3 className="font-bold text-white">AI Lead Results ({aiLeadResults.length})</h3></div>
                <div className="overflow-x-auto">
                 <table className="w-full text-sm text-left">
                    <thead className="text-xs text-kala-400 uppercase bg-kala-800/60">
                      <tr>
                        <th className="px-6 py-3">Name</th>
                        <th className="px-6 py-3">Reason</th>
                        <th className="px-6 py-3 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                        {aiLeadResults.map((lead) => (
                        <tr key={lead.id} className="border-b border-kala-800 hover:bg-kala-800/50">
                            <td className="px-6 py-4 font-medium text-white">{lead.name}</td>
                            <td className="px-6 py-4 text-kala-300">{lead.reason}</td>
                            <td className="px-6 py-4 text-center">
                            <button 
                              onClick={() => handleAddLeadFromAI(lead)} 
                              className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                            >
                              <UserPlus size={14} /> Add as Lead
                            </button>
                            </td>
                        </tr>
                        ))}
                    </tbody>
                 </table>
                </div>
             </div>
          )}

          {/* MusicBrainz Artist Search */}
          <div className="bg-kala-900/50 border border-kala-800 rounded-xl p-6">
            <label className="block text-sm text-kala-300 font-bold mb-2">MusicBrainz Artist Search</label>
            <div className="flex gap-2">
              <input 
                type="text" 
                value={artistQuery} 
                onChange={(e) => setArtistQuery(e.target.value)} 
                className="flex-grow bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white placeholder-kala-500 focus:outline-none focus:border-kala-500" 
                placeholder="Enter artist name" 
              />
              <button 
                onClick={handleArtistSearch} 
                disabled={isSearchingArtist} 
                className="bg-teal-600 hover:bg-teal-500 disabled:bg-teal-700 text-white font-bold px-6 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                {isSearchingArtist ? <RefreshCw className="animate-spin" size={18} /> : <Search size={18} />}
                {isSearchingArtist ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>
          
          {/* Artist Search Results */}
          {artistResults.length > 0 && (
            <div className="bg-kala-800/40 border border-kala-700/80 rounded-xl">
              <div className="p-4 border-b border-kala-700"><h3 className="font-bold text-white">Artist Search Results ({artistResults.length})</h3></div>
              <div className="overflow-x-auto">
                 <table className="w-full text-sm text-left">
                   <thead className="text-xs text-kala-400 uppercase bg-kala-800/60">
                     <tr>
                       <th className="px-6 py-3">Name</th>
                       <th className="px-6 py-3">Bio</th>
                       <th className="px-6 py-3 text-center">Action</th>
                     </tr>
                   </thead>
                   <tbody>
                     {artistResults.map((artist) => (
                       <tr key={artist.id} className="border-b border-kala-800 hover:bg-kala-800/50">
                         <td className="px-6 py-4 font-medium text-white">{artist.name}</td>
                         <td className="px-6 py-4 text-kala-300">{artist.bio}</td>
                         <td className="px-6 py-4 text-center">
                           <button 
                             onClick={() => handleAddLead(artist)} 
                             className="bg-teal-600 hover:bg-teal-500 text-white font-bold px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                           >
                             <UserPlus size={14} /> Add
                           </button>
                         </td>
                       </tr>
                     ))}
                   </tbody>
                 </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* --- SAVED LEADS TAB --- */}
      {activeTab === 'leads' && (
        <div className="bg-kala-800/40 border border-kala-700/80 rounded-xl">
          <div className="p-4 flex justify-between items-center border-b border-kala-700">
             <h3 className="font-bold text-white">Saved Leads ({savedLeads.length})</h3>
             <button 
               onClick={openManualAddModal} 
               className="bg-teal-600 hover:bg-teal-500 text-white font-bold px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
             >
               <PlusCircle size={18} /> Add Manual Lead
             </button>
           </div>
           <div className="overflow-x-auto">
             <table className="w-full text-sm text-left">
                 <thead className="text-xs text-kala-400 uppercase bg-kala-800/60">
                   <tr>
                     <th className="px-6 py-3">Name</th>
                     <th className="px-6 py-3">Contact</th>
                     <th className="px-6 py-3">Status</th>
                     <th className="px-6 py-3 text-center">Actions</th>
                   </tr>
                 </thead>
                 <tbody>
                   {isLoadingData ? (
                     <tr><td colSpan={4} className="px-6 py-4 text-center text-kala-400">Loading...</td></tr>
                   ) : savedLeads.length === 0 ? (
                     <tr><td colSpan={4} className="px-6 py-4 text-center text-kala-400">No leads yet. Create one or search to add leads.</td></tr>
                   ) : (
                     savedLeads.map((lead) => (
                       <tr key={lead.id} className="border-b border-kala-800 hover:bg-kala-800/50">
                         <td className="px-6 py-4 font-medium text-white">{lead.name}</td>
                         <td className="px-6 py-4 text-kala-300">
                           {lead.email && <div className="flex items-center gap-2"><Mail size={14} /> <a href={`mailto:${lead.email}`} className="hover:text-indigo-400">{lead.email}</a></div>}
                           {lead.phone && <div className="flex items-center gap-2"><Phone size={14} /> {lead.phone}</div>}
                           {lead.website && <div className="flex items-center gap-2"><Globe size={14} /> <a href={lead.website} target="_blank" rel="noopener noreferrer" className="hover:text-indigo-400">Website</a></div>}
                         </td>
                         <td className="px-6 py-4"><span className={`px-2 py-1 text-xs font-medium rounded-full bg-blue-900 text-blue-300`}>{lead.status}</span></td>
                         <td className="px-6 py-4 text-center">
                           <button 
                             onClick={() => openEnrichModal(lead)} 
                             className="text-indigo-400 hover:text-indigo-300 font-semibold transition-colors"
                           >
                             Enrich / Edit
                           </button>
                         </td>
                       </tr>
                     ))
                   )}
                 </tbody>
             </table>
           </div>
       </div>
      )}

      {/* --- SAVED QUERIES TAB --- */}
      {activeTab === 'queries' && (
        <div className="bg-kala-800/40 border border-kala-700/80 rounded-xl">
           <div className="p-4 border-b border-kala-700"><h3 className="font-bold text-white">Saved Queries ({savedQueries.length})</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                  <thead className="text-xs text-kala-400 uppercase bg-kala-800/60">
                      <tr>
                          <th className="px-6 py-3">Query</th>
                          <th className="px-6 py-3">Date</th>
                          <th className="px-6 py-3 text-center">Actions</th>
                      </tr>
                  </thead>
                  <tbody>
                      {savedQueries.map((query) => (
                          <tr key={query.id} className="border-b border-kala-800 hover:bg-kala-800/50">
                              <td className="px-6 py-4 font-medium text-white">"{query.prompt}"</td>
                              <td className="px-6 py-4 text-kala-300">{new Date(query.createdAt).toLocaleDateString()}</td>
                              <td className="px-6 py-4 text-center">
                                <div className="flex items-center justify-center gap-4">
                                  <button 
                                    onClick={() => { setAILeadQuery(query.prompt); setActiveTab('discover'); }} 
                                    className="font-semibold text-indigo-400 hover:text-indigo-300 transition-colors"
                                  >
                                    Re-run
                                  </button>
                                  <button 
                                    onClick={() => handleDeleteQuery(query.id)} 
                                    className="text-kala-500 hover:text-red-500 transition-colors" 
                                    title="Delete Query"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </td>
                          </tr>
                      ))}
                  </tbody>
              </table>
            </div>
        </div>
      )}

    </div>
  );
};

export default AdminLeads;
